import { useMutation, useQueryClient } from '@tanstack/react-query';
import { changeNodeCommentResolution } from '@/services/comment.service';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

export const useChangeNodeCommentResolution = (_nodeId: string) => {
  const tToasts = useTranslations('toasts');
  const tCommon = useTranslations('common');
  const tErrors = useTranslations('errors');

  const { toast } = useToast();

  const changeNodeCommentResolutionWithAuth = useAuthenticatedQueryFn(changeNodeCommentResolution);
  const queryClient = useQueryClient();

  const changeNodeCommentResolutionMutation = useMutation({
    mutationFn: changeNodeCommentResolutionWithAuth,
    onSuccess: (_response, { resolve }) => {
      toast({
        title: tToasts('changeNodeCommentResolutionMutation.title', {
          resolved: resolve ? 'resolved' : 'unresolved',
        }),
        description: tToasts('changeNodeCommentResolutionMutation.description', {
          resolved: resolve ? 'resolved' : 'unresolved',
        }),
      });
    },
    onError: async (err: any) => {
      // Invalidate in case the user tries to resolve/unresolve a deleted comment.
      const collaborationDisabled = err.status === 403;

      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: collaborationDisabled
          ? tToasts('changeNodeCommentResolutionMutation.collaborationDisabledTitle')
          : tCommon('error'),
        description: collaborationDisabled
          ? tToasts('changeNodeCommentResolutionMutation.collaborationDisabledDescription')
          : err.status === 404
            ? tToasts('changeNodeCommentResolutionMutation.error')
            : (err.message ?? tErrors('toastDefaultDescription')),
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.NodeCommentList],
      });
    },
  });

  return {
    changeNodeCommentResolutionMutation,
  };
};
